"use client";

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Avatar as AntdAvatar } from "antd";

import {
  AVATAR_COLOR_MAP,
  AVATAR_OPACITY,
  AVATAR_STYLED_PROPS,
} from "../constants";
import { AvatarColor, AvatarStatus } from "../interfaces";
import { FADE_IN, RIPPLE_OUT } from "../keyframes";

// Replace the pulsate animation with ripple animation
const ripple = css`
  @keyframes rippleEffect {
    0% {
      box-shadow: 0 0 0 0 var(--avatar-active-outer-shadow-base);
      transform: scale(1);
    }
    50% {
      transform: scale(1);
    }
    70% {
      box-shadow: 0 0 0 10px var(--avatar-active-outer-shadow-light);
      transform: scale(1);
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
      transform: scale(1);
    }
  }
`;

export interface StyledAvatarProps {
  $color: AvatarColor;
  $status?: AvatarStatus;
  $stroke?: boolean;
  $hoverable?: boolean;
}

export const StyledAvatar = styled(AntdAvatar, {
  shouldForwardProp: prop =>
    !AVATAR_STYLED_PROPS.IMAGE.includes(prop as keyof StyledAvatarProps),
})<StyledAvatarProps>`
  ${ripple}

  &.ant-avatar {
    position: relative;
    transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1); // Smoother transitions
    border: none !important;
    ${({ $status }) => css`
      opacity: ${$status === "active"
        ? AVATAR_OPACITY.active
        : $status === "disabled"
          ? AVATAR_OPACITY.disabled
          : 1} !important;
    `}

    ${({ $hoverable, $status }) =>
      $hoverable &&
      $status !== "disabled" &&
      css`
        &:hover {
          opacity: ${AVATAR_OPACITY.hover} !important;
          transition:
            background 0.1s cubic-bezier(0.34, 1.56, 0.64, 1),
            opacity 0.1s cubic-bezier(0.34, 1.56, 0.64, 1); // Bouncy transition
        }
      `}

    animation: ${FADE_IN} 0.2s ease-in-out;

    ${({ $status, $color }) =>
      $status === "active" &&
      css`
        --avatar-active-outer-shadow-base: ${AVATAR_COLOR_MAP[$color]
          .activeOuterShadow};
        --avatar-active-outer-shadow-medium: ${AVATAR_COLOR_MAP[$color]
          .activeOuterShadow50};
        --avatar-active-outer-shadow-light: ${AVATAR_COLOR_MAP[$color]
          .activeOuterShadow100};
        animation: rippleEffect 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      `}

    &::before {
      content: "";
      position: absolute;
      top: -5px;
      right: -5px;
      bottom: -5px;
      left: -5px;
      border-radius: 50%;
      z-index: -1;
      opacity: 0;

      ${({ $status, $color }) =>
        $status === "active" &&
        css`
          background-color: transparent;
          border: 2px solid ${AVATAR_COLOR_MAP[$color].activeOuterShadow};
          animation: ${RIPPLE_OUT} 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        `}
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 50%;
      transition:
        opacity 0.25s ease,
        border 0.25s ease;

      ${({ $status, $color, $stroke }) =>
        $stroke &&
        css`
          border: 1px solid
            ${$status === "active"
              ? AVATAR_COLOR_MAP[$color].activeStroke
              : $status === "disabled"
                ? AVATAR_COLOR_MAP[$color].disabledStroke
                : AVATAR_COLOR_MAP[$color].stroke} !important;
        `}
    }
  }
`;
